import "@/i18n/config";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState } from "react";
import { AiFillLike } from "react-icons/ai";
import { FaHome, FaMobileAlt } from "react-icons/fa";
import { FiPlusCircle, FiMinusCircle } from "react-icons/fi";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { MdWork } from "react-icons/md";
import { SiGreenhouse } from "react-icons/si";
import { TbFileCv } from "react-icons/tb";

import { CircularScore } from "@/organization_app/features/jobs/components/CircularScore";
import { Checkbox } from "@/ui/Checkbox";

interface Props {
  application: object;
  selected: boolean;
  onSelect: () => void;
}

const limitLine = 4;
export function ApplicationsItem(props: Props) {
  const { application, selected, onSelect } = props;
  const [showAll, setShowAll] = useState(false);
  const experiences = application["experiences"] || [];
  const skills = application["skills"] || [];
  const visibleExperiences = showAll
    ? experiences
    : experiences.slice(0, limitLine);
  const visibleSkills = showAll ? skills : skills.slice(0, limitLine);
  return (
    <>
      <tr className='border-gray-300 border-t'>
        <td className='pl-2' style={{ verticalAlign: "top" }}>
          <Checkbox
            checked={selected}
            onChange={() => {
              onSelect();
            }}
          />
        </td>
        <td className='p-4' style={{ verticalAlign: "top" }}>
          <CircularScore score={application["rating"]} />
          <h2 className='text-xl font-semibold pt-2'>{application["name"]}</h2>
          {application["state"] && (
            <div className='flex pt-1'>
              <FaHome color='#6b7280' size={14} />
              <div className='text-gray-500 text-xs pl-1 font-semibold'>
                {application["state"]} ({application["country_code"]})
              </div>
            </div>
          )}
          {application["mobile_number"] && (
            <div className='flex pt-1'>
              <FaMobileAlt color='#6b7280' size={14} />
              <div className='text-gray-500 text-xs pl-1 font-semibold'>
                {application["mobile_number"]}
              </div>
            </div>
          )}
          {application["email"] && (
            <div className='flex pt-1'>
              <MdOutlineAlternateEmail color='#6b7280' size={14} />
              <div className='text-gray-500 text-xs pl-1 font-semibold'>
                {application["email"]}
              </div>
            </div>
          )}
          <div className='flex pt-1'>
            <SiGreenhouse color='#6b7280' size={14} />
            <a
              className='text-gray-500 text-xs pl-1 underline font-semibold'
              href={application["greenhouse_url"]}
            >
              {application["gh_id"]}
            </a>
          </div>
          {application["resume_url"] && (
            <div className='flex pt-1'>
              <TbFileCv color='#6b7280' size={14} />
              <a
                className='text-gray-500 text-xs pl-1 underline font-semibold'
                href={application["resume_url"]}
              >
                Resume
              </a>
            </div>
          )}
        </td>

        <motion.td className='p-4' style={{ verticalAlign: "top" }}>
          <AnimatePresence initial={false}>
            {visibleExperiences.map((exp, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                key={index}
                className='flex mb-2 gap-1 text-gray-700'
              >
                <div className='pt-1'>
                  <MdWork color='#6b7280' size={14} />
                </div>
                <div>
                  <div className='text-sm'>
                    <strong className='uppercase'>{exp["title"]}</strong>
                    <span> at </span>
                    <strong className='uppercase'>{exp["company"]}</strong>
                  </div>
                  <div className='text-xs text-gray-500 font-semibold'>
                    {exp["period"]}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.td>

        <motion.td className='p-4' style={{ verticalAlign: "top" }}>
          {visibleSkills && (
            <motion.div>
              <div className='flex pb-2'>
                <div className='pr-1 pt-[2px]'>
                  <AiFillLike size={14} />
                </div>
                <div className='font-semibold text-gray-700 text-sm uppercase font-semibold'>
                  Suitability Skills
                </div>
              </div>
              <ul className='text-sm text-gray-600 list-disc ml-4'>
                <AnimatePresence initial={false}>
                  {visibleSkills.map((skills, index) => (
                    <motion.li
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      key={index}
                    >
                      <div>
                        <div>{skills}</div>
                      </div>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            </motion.div>
          )}
        </motion.td>

        <td className='p-4' style={{ verticalAlign: "top" }}>
          {application["unique_selling_points"] && (
            <div className='pb-2'>
              <div className='flex pb-1'>
                <div className='pr-1 pt-[2px]'>
                  <FiPlusCircle size={14} color='green' />
                </div>
                <div className='font-semibold text-gray-700 text-sm uppercase font-semibold'>
                  Unique Selling Points
                </div>
              </div>
              <ul className='text-gray-600 list-disc ml-4'>
                {application["unique_selling_points"].map((point, index) => (
                  <li key={index}>
                    <div>
                      <div className='text-sm'>{point}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {application["areas_for_improvement"] && (
            <div>
              <div className='flex pb-1'>
                <div className='pr-1 pt-[2px]'>
                  <FiMinusCircle size={14} color='red' />
                </div>
                <div className='font-semibold text-gray-700 text-sm uppercase font-semibold'>
                  Areas For Improvement
                </div>
              </div>

              <ul className='text-gray-600 list-disc ml-4'>
                {application["areas_for_improvement"].map((point, index) => (
                  <li key={index}>
                    <div>
                      <div className='text-sm'>{point}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </td>
      </tr>
      {(skills.length > limitLine || experiences.length > limitLine) && (
        <tr>
          <td colSpan={12} style={{ textAlign: "center", paddingTop: "1rem" }}>
            <div
              className='cursor-pointer underline'
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "See less" : "See more"}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
